import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { useLang, route, postPath } from "../i18n";
import { useCompany, useSitePosts } from "../components/SiteContext";
import SiteImage from "../components/SiteImage";
import { CREDENTIALS } from "../data/config";
import { CATEGORIES } from "../data/posts";
import PageHero from "../components/PageHero";
import { SectionHead } from "../components/SectionHead";
import { Reveal } from "../components/Reveal";
import FinalCta from "../components/FinalCta";

const Author = () => {
  const { lang, t } = useLang();
  const company = useCompany();
  const allPosts = useSitePosts();
  const a = t.authorPage;
  const articles = allPosts.filter((p) => p.status === "published");

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: company.founder,
    jobTitle: a.role,
    worksFor: { "@type": "Organization", name: company.name },
  };

  return (
    <>
      <Seo title={a.seo.title} siteName={company.name} description={a.seo.description} jsonLd={jsonLd} />
      <PageHero
        eyebrow={a.eyebrow}
        lines={[company.founder]}
        copy={a.role}
        breadcrumb={[{ label: t.nav.home, to: route(lang, "home") }, { label: company.founder }]}
        testid="author-hero"
      />

      <section className="bg-[#F4F0E8]">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28">
          <div className="grid gap-16 lg:grid-cols-[1fr_1.3fr]">
            <Reveal>
              <div className="relative">
                <div className="absolute -left-3 -top-3 h-full w-full border border-[#C6A46A]" aria-hidden="true" />
                <SiteImage
                  slot="leader"
                  alt="Professional portrait setting"
                  className="relative"
                  imgClassName="aspect-[4/5] w-full object-cover grayscale-[30%]"
                />
              </div>
            </Reveal>
            <div>
              <SectionHead eyebrow={a.bioTitle} tone="light" />
              <Reveal delay={0.1}>
                <p className="mt-6 text-sm leading-relaxed text-[#171717]/75 sm:text-base">{t.home.founder.bioP1}</p>
                <p className="mt-5 text-sm leading-relaxed text-[#171717]/75 sm:text-base">{t.home.founder.bioP2}</p>
              </Reveal>
              <Reveal delay={0.2}>
                <ul className="mt-10 grid gap-3 sm:grid-cols-2">
                  {CREDENTIALS[lang].map((c, i) => (
                    <li key={i} className="flex items-center gap-3 border border-[#171717]/10 px-4 py-3 text-xs uppercase tracking-[0.12em] text-[#171717]/70">
                      <span className="h-1 w-1 rounded-full bg-[#C6A46A]" />
                      {c}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
          </div>

          <div className="mt-24">
            <SectionHead eyebrow={a.articles} tone="light" />
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {articles.map((p) => (
                <Link
                  key={p.slug}
                  to={postPath(lang, p.slug)}
                  className="group flex items-center justify-between gap-4 border border-[#171717]/12 p-5 transition-colors hover:border-[#C6A46A]"
                >
                  <div>
                    <span className="text-[0.6rem] uppercase tracking-[0.18em] text-[#8a6f3e]">
                      {CATEGORIES[p.category]?.[lang] || p.category}
                    </span>
                    <p className="mt-1 text-sm font-normal text-[#171717]">{p[lang]?.title}</p>
                  </div>
                  <ArrowRight className="h-4 w-4 shrink-0 text-[#C6A46A] transition-transform group-hover:translate-x-1" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <FinalCta />
    </>
  );
};

export default Author;
